import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, ErrorBanner, Pill } from "../components/ui";
import { api, type Campaign, type Template } from "../lib/api";

type Step = {
  day: number;
  // The first step of a day OPENS that day at `at`. Every later step that day
  // is `afterHours` after the one before it actually went out. Absolute for
  // the anchor, relative for the follow-ups — which is what stops a contact
  // who joined at 2pm receiving every later day three hours late for ever.
  at: string;
  afterHours: number;
  body: string;
  templateId: number | null;
  // Write this step for each person rather than sending the copy above. The
  // copy stays, and becomes the fallback for anyone whose message cannot be
  // written - so this can never leave a step with nothing to send.
  personalize: boolean;
  brief: string;
};

const AFTER = [1, 2, 3, 4, 5, 6];

// Whether this step opens its day. Derived from position rather than stored,
// so dragging a step between days cannot leave two anchors or none.
const isDayAnchor = (steps: Step[], i: number) =>
  i === 0 || steps[i - 1].day !== steps[i].day;

type Rule = {
  trigger: string;
  params: Record<string, string>;
  // Carried through untouched. This screen cannot edit a condition yet, and
  // dropping what it cannot show would silently widen a rule aimed at a few
  // people to everyone the trigger fires for.
  condition: Record<string, unknown> | null;
  to: number | null;
  delay: number;
  addTags: string;
  removeTags: string;
  // Off means the rule acts without ending the cadence they are in - tag them,
  // or start a second campaign alongside, and let the drip carry on.
  exitSource: boolean;
  // The three fields this screen has no control for. Carried through for the
  // same reason the condition is: a save that posted the defaults back deleted
  // and rebuilt every rule, so priority 60 became 0, a rule someone had
  // deliberately switched off came back on, and a target campaign that was
  // meant to start at its third message started at its first.
  priority: number;
  enabled: boolean;
  startAtStep: number;
};

type TriggerParam = { key: string; label: string; type: string; default: string | number };
type Trigger = {
  key: string;
  label: string;
  hint: string;
  live: boolean;
  params?: TriggerParam[];
};

const BLANK_RULE: Rule = {
  trigger: "reply_received",
  params: {},
  condition: null,
  to: null,
  delay: 0,
  addTags: "",
  removeTags: "",
  exitSource: true,
  priority: 0,
  enabled: true,
  startAtStep: 0,
};

const tagList = (s: string) =>
  s.split(",").map((t) => t.trim()).filter(Boolean);

const OPS: Record<string, string> = {
  eq: "is", ne: "is not", gt: "is over", gte: "is at least",
  lt: "is under", lte: "is at most", within: "is within", has_tag: "is tagged",
};

// Enough of a condition to recognise it. Conditions are set outside this
// screen for now, so the risk is not that someone mis-edits one - it is that
// a rule looks unconditional when it is not.
function describeCondition(node: Record<string, unknown>): string {
  const kids = (node.all ?? node.any) as Record<string, unknown>[] | undefined;
  if (Array.isArray(kids)) {
    return kids.map(describeCondition).join(node.all ? " and " : " or ");
  }
  const field = String(node.field ?? "").replace(/^contact\./, "").replace(/_/g, " ");
  const op = OPS[String(node.op)] ?? String(node.op ?? "");
  return `${field} ${op} ${node.value ?? ""}`.trim();
}

// A rule read back as a sentence. Four controls that each look reasonable can
// still add up to something nobody meant - "tag them and stop the cadence and
// start nothing" is a dead end that only reads as one when it is spelled out.
function describe(r: Rule, t: Trigger | undefined, others: Campaign[]): string {
  const when = (t?.label ?? r.trigger).replace(/^They /, "they ").toLowerCase();
  const params = Object.entries(r.params)
    .filter(([, v]) => v !== "")
    .map(([k, v]) => (k === "days" ? `${v} days` : `"${v}"`));
  const cond = r.condition
    ? ` and they match ${describeCondition(r.condition)}`
    : "";
  const head = `When ${when}${params.length ? ` (${params.join(", ")})` : ""}${cond}`;

  const acts: string[] = [];
  const target = others.find((c) => c.id === r.to);
  if (target) {
    const wait = WHEN.find((w) => w.minutes === r.delay);
    acts.push(
      `start them on ${target.name}${
        r.delay ? ` ${wait?.label ?? `${r.delay} min later`}` : ""
      }`,
    );
  }
  const added = tagList(r.addTags);
  const removed = tagList(r.removeTags);
  if (added.length) acts.push(`tag them ${added.join(" + ")}`);
  if (removed.length) acts.push(`remove ${removed.join(" + ")}`);
  if (r.exitSource) acts.push("take them out of this cadence");

  const sentence = !acts.length
    ? `${head}, nothing happens — this rule has no effect.`
    : `${head}, ${acts.join(", ")}.`;
  // A rule someone switched off reads exactly like a live one unless it says
  // so, and this screen has no switch to look at.
  return r.enabled ? sentence : `Switched off, so this never fires. ${sentence}`;
}

const WHEN = [
  { minutes: 0, label: "straight away" },
  { minutes: 60, label: "an hour later" },
  { minutes: 1440, label: "the next day" },
  { minutes: 2880, label: "two days later" },
  { minutes: 10080, label: "a week later" },
  { minutes: 43200, label: "thirty days later" },
];

const TYPES = [
  { key: "waterfall", label: "Waterfall", hint: "Day 0 onward until they reply" },
  { key: "no_cc", label: "No connected call", hint: "Replied, never reached by phone" },
  { key: "cc_no_sale", label: "Connected, no sale", hint: "Spoke to them, did not close" },
  { key: "upsell", label: "Upsell", hint: "Already bought" },
  { key: "reminder", label: "Reminder", hint: "Appointment nudges" },
];

const GSM = /^[A-Za-z0-9 \r\n@£$¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ!"#¤%&'()*+,\-./:;<=>?¡ÄÖÑÜ§¿äöñüà^{}\\[~\]|€]*$/;

function segments(body: string): { n: number; unicode: boolean } {
  const unicode = !GSM.test(body);
  const limit = unicode ? 70 : 160;
  const multi = unicode ? 67 : 153;
  if (body.length === 0) return { n: 0, unicode };
  return { n: body.length <= limit ? 1 : Math.ceil(body.length / multi), unicode };
}

// A step's wait is the gap from the previous step, in minutes - so an
// afternoon follow-up on the same day is just a small wait, not a special
// case. The time of day is pinned by giving the step a narrow send window.
function toPayloadSteps(steps: Step[]) {
  return steps.map((s, i) => {
    const anchor = isDayAnchor(steps, i);
    return {
      name: anchor ? `Day ${s.day} ${s.at}` : `Day ${s.day} +${s.afterHours}h`,
      day_offset: s.day,
      delay_from: anchor ? "day_time" : "previous",
      at_time: anchor ? s.at : undefined,
      delay_minutes: anchor ? 0 : Math.max(1, s.afterHours) * 60,
      template_id: s.templateId,
      // Always send the copy, even when the step already has a template. The
      // API updates that template in place when the wording changed; sending
      // only the id would make an edit silently do nothing, and sending only
      // the body mints a duplicate template on every save.
      body: s.body || undefined,
      personalize: s.personalize,
      personalize_brief: s.personalize ? s.brief : undefined,
      // The window still bounds the step; the anchor and the offset decide
      // when inside it the message actually goes.
      send_window_start: anchor ? `${s.at}:00` : undefined,
      send_window_end: undefined,
    };
  });
}

export function CampaignNew() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = id ? Number(id) : null;

  const [name, setName] = useState("");
  const [type, setType] = useState("waterfall");
  const [templates, setTemplates] = useState<Template[]>([]);
  const [steps, setSteps] = useState<Step[]>([
    { day: 0, at: "11:00", afterHours: 1, body: "", templateId: null, personalize: false, brief: "" },
    { day: 1, at: "11:00", afterHours: 1, body: "", templateId: null, personalize: false, brief: "" },
  ]);
  // Whether the very first message waits for Day 0's clock time or goes as
  // soon as the contact enters. It is the answer to "they signed up at 2pm
  // but Day 0 says noon" and the two answers are genuinely different, so it
  // is a choice rather than a default buried in the engine.
  const [firstMode, setFirstMode] = useState<"immediate" | "at_time">("immediate");
  const [rules, setRules] = useState<Rule[]>([]);
  const [triggers, setTriggers] = useState<Trigger[]>([]);
  const [others, setOthers] = useState<Campaign[]>([]);
  const [live, setLive] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.templates().then(setTemplates).catch((e) => setError(e.message));
    api
      .campaigns()
      .then((cs) => setOthers(cs.filter((c) => c.id !== editing)))
      .catch(() => setOthers([]));
    api
      .transitions()
      .then((t) => setTriggers(t.triggers))
      .catch(() => setTriggers([]));

    if (!editing) return;
    api
      .campaignDetail(editing)
      .then((d) => {
        setName(d.name);
        setType(d.campaign_type);
        setLive(d.active_enrollments);
        setFirstMode(d.first_message_mode === "at_time" ? "at_time" : "immediate");
        setSteps(
          d.steps.map((s) => ({
            day: s.day_offset,
            at: s.at_time ?? s.send_window_start,
            afterHours: Math.max(1, Math.round((s.delay_minutes || 60) / 60)),
            body: s.body ?? "",
            // Keep the template id. This used to drop it whenever the step
            // had a body - which is every real step - so saving posted the
            // body with no template_id and the API made a BRAND NEW template
            // for every step, every time. Opening a campaign and pressing
            // save without touching anything duplicated all its copy and
            // orphaned the originals; the library had grown to 219 templates
            // that way, which is also what made the copy screen take a
            // minute to load.
            templateId: s.template_id ?? null,
            personalize: !!s.personalize,
            brief: s.personalize_brief ?? "",
          })),
        );
        setRules(
          d.transitions.map((t) => ({
            trigger: t.trigger,
            params: Object.fromEntries(
              Object.entries(t.trigger_params ?? {}).map(([k, v]) => [k, String(v)]),
            ),
            condition: t.condition,
            to: t.to_campaign_id,
            delay: t.delay_minutes,
            addTags: (t.add_tags ?? []).join(", "),
            removeTags: (t.remove_tags ?? []).join(", "),
            exitSource: t.exit_source ?? true,
            priority: t.priority ?? 0,
            enabled: t.enabled ?? true,
            startAtStep: t.start_at_step_index ?? 0,
          })),
        );
      })
      .catch((e) => setError(e.message));
  }, [editing]);

  const set = (i: number, patch: Partial<Step>) =>
    setSteps((ss) => ss.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  const addSameDay = (i: number) =>
    setSteps((ss) => {
      const prev = ss[i];
      const [h] = prev.at.split(":").map(Number);
      const next: Step = {
        day: prev.day,
        at: `${String(Math.min(21, h + 2)).padStart(2, "0")}:00`,
        afterHours: 2,
        body: "",
        templateId: null,
        // A new step inherits how the one it follows is written. Turning a
        // campaign personalised and then adding a follow-up that quietly is
        // not would be a surprise nobody would look for.
        personalize: prev.personalize,
        brief: prev.brief,
      };
      return [...ss.slice(0, i + 1), next, ...ss.slice(i + 1)];
    });

  const addNextDay = () =>
    setSteps((ss) => [
      ...ss,
      {
        day: (ss[ss.length - 1]?.day ?? 0) + 1,
        at: "11:00",
        afterHours: 2,
        body: "",
        templateId: null,
        personalize: ss[ss.length - 1]?.personalize ?? false,
        brief: ss[ss.length - 1]?.brief ?? "",
      },
    ]);

  // Steps must move forward in time. Going backwards would make the wait
  // negative, which the scheduler reads as "send now" - a burst instead of a
  // cadence.
  // Only the day numbers have to move forward now. A follow-up is "+N hours
  // after the last one", which cannot go backwards by construction, and the
  // times on two different days are independent - Day 1 at 11:00 following
  // Day 0 at 16:00 is perfectly ordinary and used to be flagged as an error.
  const outOfOrder = useMemo(
    () => steps.some((s, i) => i > 0 && s.day < steps[i - 1].day),
    [steps],
  );

  const empty = steps.filter((s) => !s.body.trim() && !s.templateId).length;
  const lastDay = steps.length ? Math.max(...steps.map((s) => s.day)) : 0;

  const save = (publish: boolean) => {
    setBusy(true);
    setError(null);
    const body = {
      name,
      campaign_type: type,
      first_message_mode: firstMode,
      steps: toPayloadSteps(steps),
      transitions: rules.map((r) => ({
        trigger: r.trigger,
        trigger_params: r.params,
        condition: r.condition,
        to_campaign_id: r.to,
        delay_minutes: r.delay,
        add_tags: tagList(r.addTags),
        remove_tags: tagList(r.removeTags),
        exit_source: r.exitSource,
        // Sent back exactly as they were read. The save path rebuilds every
        // rule from this payload, so anything missing here is not "left
        // alone" - it is reset to the API's default.
        priority: r.priority,
        enabled: r.enabled,
        start_at_step_index: r.startAtStep,
      })),
    };
    const saved = editing
      ? api.editCampaign(editing, body)
      : api.createCampaign(body);
    saved
      .then((c) => (publish ? api.publishCampaign(c.id) : c))
      .then(() => navigate("/campaigns"))
      .catch((e) => {
        setError(e.message);
        setBusy(false);
      });
  };

  return (
    <div className="sidebar">
      <div className="col">
        <ErrorBanner error={error} />
        <h2 style={{ fontSize: 19 }}>{editing ? "Edit campaign" : "New campaign"}</h2>

        <Card title="1 · What it is">
          <div className="col">
            <input
              className="f"
              placeholder="Campaign name, e.g. Summer waterfall"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <select className="f" value={type} onChange={(e) => setType(e.target.value)}>
              {TYPES.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.label} — {t.hint}
                </option>
              ))}
            </select>
          </div>
        </Card>

        <Card
          title="2 · The cadence"
          footer={
            <span className="dim">
              Day 0 is the day they enter, not a calendar date. The first message of
              each day is pinned to a time; the rest follow it by hours, counted from
              when the one before actually went out. So a lead who joins at 2pm still
              gets Day 1 at its proper hour, and their follow-ups still land the
              spacing you asked for.
            </span>
          }
        >
          <div className="banner stack" style={{ marginBottom: 12 }}>
            <div className="row w" style={{ gap: 8 }}>
              <span className="lab">The very first message goes out</span>
              <select
                className="f"
                value={firstMode}
                onChange={(e) => setFirstMode(e.target.value as "immediate" | "at_time")}
              >
                <option value="immediate">as soon as they enter</option>
                <option value="at_time">at Day 0's time, even if that means tomorrow</option>
              </select>
            </div>
            <span className="dim" style={{ fontSize: 11.5 }}>
              {firstMode === "immediate"
                ? `Someone entering at 2pm hears from you at 2pm, then +${steps[0]?.afterHours ?? 1}h from there. Right for a welcome.`
                : `Someone entering at 2pm waits until ${steps[0]?.at ?? "11:00"} tomorrow. Right when the hour is the point.`}
            </span>
          </div>
          {outOfOrder && (
            <div className="banner bad stack" style={{ marginBottom: 12 }}>
              <b>Steps have to move forward in time.</b> Two at the same moment, or one
              that goes backwards, would fire together instead of spacing out.
            </div>
          )}

          <div className="col" style={{ gap: 10 }}>
            {steps.map((s, i) => {
              const seg = segments(s.body);
              return (
                <div key={i} className="crow" style={{ alignItems: "flex-start" }}>
                  <div className="row" style={{ gap: 6, flexWrap: "wrap", width: 232 }}>
                    <span className="lab" style={{ width: 28 }}>Day</span>
                    <input
                      className="f"
                      type="number"
                      min={0}
                      style={{ width: 58 }}
                      value={s.day}
                      onChange={(e) => set(i, { day: Number(e.target.value) })}
                    />
                    {isDayAnchor(steps, i) ? (
                      <>
                        <span className="lab">at</span>
                        <input
                          className="f"
                          type="time"
                          value={s.at}
                          onChange={(e) => set(i, { at: e.target.value })}
                        />
                      </>
                    ) : (
                      <>
                        <select
                          className="f"
                          style={{ width: 62 }}
                          value={s.afterHours}
                          onChange={(e) => set(i, { afterHours: Number(e.target.value) })}
                        >
                          {AFTER.map((h) => (
                            <option key={h} value={h}>+{h}h</option>
                          ))}
                        </select>
                        <span className="lab">after the last one</span>
                      </>
                    )}
                  </div>

                  <div className="col" style={{ flex: "1 1 320px", gap: 6, minWidth: 0 }}>
                    <label
                      className="row"
                      style={{ gap: 7, fontSize: 11.5, cursor: "pointer" }}
                    >
                      <input
                        type="checkbox"
                        checked={s.personalize}
                        onChange={(e) => set(i, { personalize: e.target.checked })}
                      />
                      <span>Send a customized message</span>
                      {s.personalize && (
                        <span className="dim">
                          the copy below becomes the fallback
                        </span>
                      )}
                    </label>
                    {s.personalize && (
                      <input
                        className="f"
                        style={{ fontSize: 11.5 }}
                        placeholder="What is this step for? e.g. last try before we stop"
                        value={s.brief}
                        onChange={(e) => set(i, { brief: e.target.value })}
                      />
                    )}
                    {s.templateId ? (
                      <div className="row w">
                        <Pill tone="ac">
                          {templates.find((t) => t.id === s.templateId)?.name ?? "template"}
                        </Pill>
                        <button className="btn s" onClick={() => set(i, { templateId: null })}>
                          Write my own instead
                        </button>
                      </div>
                    ) : (
                      <>
                        <textarea
                          className="f"
                          style={{ minHeight: 64, width: "100%" }}
                          placeholder={
                            s.personalize
                              ? "Fallback — sent to anyone whose message can't be written."
                              : "Type the message. Use {{first_name}} for their name."
                          }
                          value={s.body}
                          onChange={(e) => set(i, { body: e.target.value })}
                        />
                        <div className="row w" style={{ fontSize: 11.5 }}>
                          <span className="dim mono">{s.body.length} chars</span>
                          {s.body && (
                            <Pill tone={seg.unicode ? "wa" : "nu"}>
                              {seg.n} text{seg.n > 1 ? "s" : ""}
                            </Pill>
                          )}
                          {seg.unicode && (
                            <span className="dim">
                              an emoji or smart quote halves the limit to 70
                            </span>
                          )}
                          {templates.length > 0 && (
                            <select
                              className="f"
                              style={{ fontSize: 11.5, marginLeft: "auto" }}
                              value=""
                              onChange={(e) =>
                                set(i, { templateId: Number(e.target.value) })
                              }
                            >
                              <option value="">or reuse a saved one…</option>
                              {templates.map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.name}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  <div className="col" style={{ gap: 4 }}>
                    <button className="btn s" onClick={() => addSameDay(i)} title="another message the same day">
                      + same day
                    </button>
                    {steps.length > 1 && (
                      <button
                        className="btn s"
                        onClick={() => setSteps((ss) => ss.filter((_, j) => j !== i))}
                      >
                        remove
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="row" style={{ marginTop: 12 }}>
            <button className="btn s" onClick={addNextDay}>
              + another day
            </button>
          </div>
        </Card>

        <Card
          title="3 · What happens when they act"
          footer={
            <span className="dim">
              These belong to this campaign. Without a rule, someone who replies simply
              exits and nothing picks them up — which is usually not what you want.
            </span>
          }
        >
          {rules.length === 0 && (
            <div className="empty" style={{ padding: "18px 12px" }}>
              No rules yet. A reply will end the cadence and stop there.
            </div>
          )}

          <div className="col" style={{ gap: 10 }}>
            {rules.map((r, i) => {
              const t = triggers.find((x) => x.key === r.trigger);
              const patch = (p: Partial<Rule>) =>
                setRules((rs) => rs.map((x, j) => (j === i ? { ...x, ...p } : x)));
              return (
                <div key={i} className="rule">
                  <div className="crow">
                    <span className="lab">When</span>
                    <select
                      className="f"
                      value={r.trigger}
                      onChange={(e) => {
                        const next = triggers.find((x) => x.key === e.target.value);
                        // Parameters belong to the trigger, so switching
                        // trigger drops them rather than carrying a stale
                        // "days: 3" onto a rule about tags.
                        patch({
                          trigger: e.target.value,
                          params: Object.fromEntries(
                            (next?.params ?? []).map((p) => [p.key, String(p.default)]),
                          ),
                        });
                      }}
                    >
                      {triggers.map((x) => (
                        <option key={x.key} value={x.key}>
                          {x.label}
                          {x.live ? "" : " (not firing yet)"}
                        </option>
                      ))}
                    </select>

                    {(t?.params ?? []).map((p) => (
                      <span key={p.key} className="row" style={{ gap: 6 }}>
                        <input
                          className="f"
                          style={{ width: p.type === "number" ? 64 : 140 }}
                          type={p.type === "number" ? "number" : "text"}
                          min={p.type === "number" ? 1 : undefined}
                          placeholder={String(p.default)}
                          value={r.params[p.key] ?? ""}
                          onChange={(e) =>
                            patch({ params: { ...r.params, [p.key]: e.target.value } })
                          }
                        />
                        <span className="lab">{p.label}</span>
                      </span>
                    ))}

                    {t && !t.live && <Pill tone="wa">not firing yet</Pill>}

                    {/* Read-only, because this screen has no control for any of
                        them - but a rule that is switched off, or that outranks
                        another, has to be visible or the next person to open
                        this page edits a campaign they cannot fully see. */}
                    {!r.enabled && <Pill tone="ba">switched off</Pill>}
                    {r.priority !== 0 && <Pill tone="nu">priority {r.priority}</Pill>}
                    {r.startAtStep > 0 && (
                      <Pill tone="nu">target starts at message {r.startAtStep + 1}</Pill>
                    )}

                    <button
                      className="x"
                      style={{ marginLeft: "auto" }}
                      onClick={() => setRules((rs) => rs.filter((_, j) => j !== i))}
                    >
                      ×
                    </button>
                  </div>

                  <div className="crow">
                    <span className="lab">then</span>
                    <select
                      className="f"
                      value={r.to ?? ""}
                      onChange={(e) =>
                        patch({ to: e.target.value ? Number(e.target.value) : null })
                      }
                    >
                      <option value="">don't start another campaign</option>
                      {others.map((c) => (
                        <option key={c.id} value={c.id}>
                          start {c.name}
                        </option>
                      ))}
                    </select>

                    {r.to !== null && (
                      <select
                        className="f"
                        value={r.delay}
                        onChange={(e) => patch({ delay: Number(e.target.value) })}
                      >
                        {WHEN.map((w) => (
                          <option key={w.minutes} value={w.minutes}>
                            {w.label}
                          </option>
                        ))}
                      </select>
                    )}

                    <label className="chk">
                      <input
                        type="checkbox"
                        checked={r.exitSource}
                        onChange={(e) => patch({ exitSource: e.target.checked })}
                      />
                      and stop this cadence
                    </label>
                  </div>

                  <div className="crow">
                    <span className="lab">tag them</span>
                    <input
                      className="f"
                      style={{ minWidth: 150 }}
                      placeholder="hot, called_back"
                      value={r.addTags}
                      onChange={(e) => patch({ addTags: e.target.value })}
                    />
                    <span className="lab">and remove</span>
                    <input
                      className="f"
                      style={{ minWidth: 150 }}
                      placeholder="unresponsive"
                      value={r.removeTags}
                      onChange={(e) => patch({ removeTags: e.target.value })}
                    />
                  </div>

                  <div className="dim" style={{ fontSize: 11.5, paddingLeft: 2 }}>
                    {describe(r, t, others)}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="row" style={{ marginTop: 10 }}>
            <button
              className="btn s"
              onClick={() => setRules((rs) => [...rs, { ...BLANK_RULE }])}
            >
              + add a rule
            </button>
            {others.length === 0 && (
              <span className="dim" style={{ fontSize: 11.5 }}>
                Only this campaign exists so far — rules can still tag people; build
                the next campaign to be able to move them into it.
              </span>
            )}
          </div>
        </Card>
      </div>

      <div className="col">
        <Card title="Ready?">
          <div className="col" style={{ gap: 12 }}>
            <div>
              <span className="lab">Messages</span>
              <div className="num" style={{ fontSize: 29, fontWeight: 650 }}>
                {steps.length}
              </div>
            </div>
            <div>
              <span className="lab">Over</span>
              <div className="num" style={{ fontSize: 22, fontWeight: 650 }}>
                {lastDay + 1} days
              </div>
            </div>

            {empty > 0 && (
              <div className="banner warn stack">
                {empty} step{empty > 1 ? "s have" : " has"} no message. Save as a draft
                now, fill them in before publishing.
              </div>
            )}

            {editing && live > 0 && (
              <div className="banner stack" style={{ background: "var(--surf2)" }}>
                <b>{live} people are part-way through this campaign.</b> Saving writes a
                new version rather than changing the one they are on, so nobody's day 5
                gets rewritten after they have already had day 2. They keep the old
                cadence; anyone entering after you publish gets this one.
              </div>
            )}

            <button
              className="btn p"
              disabled={!name || outOfOrder || busy}
              onClick={() => save(false)}
            >
              {busy ? "…" : "Save as draft"}
            </button>
            <button
              className="btn"
              disabled={!name || outOfOrder || empty > 0 || busy}
              onClick={() => save(true)}
            >
              Save and publish
            </button>
            <span className="dim" style={{ fontSize: 11.5 }}>
              A draft never sends. Publishing is what makes the scheduler start using
              this cadence.
            </span>
          </div>
        </Card>

        <Card title="Always true, whatever you set">
          <div className="col" style={{ gap: 8, fontSize: 12.5 }}>
            <div className="row w">
              <Pill tone="ba">Opt-out</Pill>
              <span className="dim">exits everything, permanently. Not configurable.</span>
            </div>
            <div className="row w">
              <Pill tone="ac">One nurture at a time</Pill>
              <span className="dim">
                the database refuses to put someone in two at once
              </span>
            </div>
            <a className="btn" href="/journeys" style={{ marginTop: 4 }}>
              See the whole funnel
            </a>
          </div>
        </Card>
      </div>
    </div>
  );
}
